import {
  useJournalEntriesMinMaxQuery,
  useEnabledFeatures,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { day } from '@vrplatform/utils';
import {
  Filter,
  type CalendarFilterDateType,
  type SelectItem,
} from '@vrplatform/components';
import { SegmentedControl } from '@mantine/core';
import { useMemo } from 'react';
import {
  type ProfitAndLossTableFilterType,
  type ProfitAndLossTableFilterView,
  useProfitAndLossTableFilter,
} from './useProfitAndLossTableFilter';
import { CustomFilter } from '@vrplatform/table-filter';

const Date = () => {
  const {
    filter: { type },
  } = useProfitAndLossTableFilter();

  if (type === 'month' || type === 'quarter' || type === 'year') {
    return <YearOrMonthRange type={type === 'year' ? 'year' : 'month'} />;
  }

  return <DateRange />;
};

const YearOrMonthRange = ({ type }: { type: 'year' | 'month' }) => {
  const { filter, setDate } = useProfitAndLossTableFilter();

  const queryData = useJournalEntriesMinMaxQuery({ range: type });
  const data = queryData.data;

  const setValue = (v: CalendarFilterDateType | undefined | null) => {
    if (!v || !v[0]) return;

    const startDate = day(v[0]).yyyymmdd();
    const endDate =
      v[0] || v[1]
        ? day(v[1] || v[0])
            .endOf(type)
            .yyyymmdd()
        : undefined;

    if (!endDate) return;

    setDate({
      startDate,
      endDate,
    });
  };

  if (type === 'month') {
    return (
      <Filter.Month
        label="Month"
        value={[day(filter.startDate).toDate(), day(filter.endDate).toDate()]}
        type="range"
        setValue={setValue}
      />
    );
  }

  return (
    <Filter.Year
      label="Year"
      value={[day(filter.startDate).toDate(), day(filter.endDate).toDate()]}
      type="range"
      minDate={data.minDate ? day(data.minDate).toDate() : undefined}
      maxDate={data.maxDate ? day(data.maxDate).toDate() : undefined}
      setValue={setValue}
    />
  );
};

const DateRange = () => {
  const { filter, setDate } = useProfitAndLossTableFilter();

  return (
    <Filter.Date
      value={[day(filter.startDate).toDate(), day(filter.endDate).toDate()]}
      clearable={false}
      setValue={(v) => {
        if (!v || !v[1] || !v[0]) return;

        setDate({
          startDate: day(v[0]).yyyymmdd(),
          endDate: day(v[1]).yyyymmdd(),
        });
      }}
      type="range"
    />
  );
};

const Book = () => {
  const { filter, setFilter } = useProfitAndLossTableFilter();
  const { OPEX } = useEnabledFeatures();

  const options: SelectItem<'trust' | 'operating'>[] = [
    {
      value: 'trust',
      label: 'Trust',
      icon: <Icon icon="BookOpenIcon" size={14} />,
    },
    {
      value: 'operating',
      label: 'Operating',
      icon: <Icon icon="BriefcaseIcon" size={14} />,
    },
  ];

  const value =
    options.find((option) => option.value === filter.ledger) ?? null;

  if (!OPEX) return null;

  return (
    <Filter.Select
      type="single"
      label="Book"
      value={value}
      data={{
        options,
      }}
      setValue={(updated) => {
        setFilter({ ledger: updated?.value || null });
      }}
      hideSearch
      popoverWidth={170}
    />
  );
};

const TYPE_OPTIONS: Record<ProfitAndLossTableFilterType, string> = {
  party: 'By Party',
  listing: 'By Listing',
  month: 'By Month',
  quarter: 'By Quarter',
  year: 'By Year',
};

const Type = () => {
  const { filter, setType } = useProfitAndLossTableFilter();

  const data = useMemo(() => {
    return Object.entries(TYPE_OPTIONS).map(([value, label]) => ({
      label,
      value,
    }));
  }, []);

  return (
    <SegmentedControl
      data={data}
      size="sm"
      value={filter.type}
      onChange={(value) => setType(value as ProfitAndLossTableFilterType)}
      styles={(theme) => ({
        innerLabel: {
          paddingInline: theme.spacing.xs,
        },
      })}
    />
  );
};

const VIEW_OPTIONS: Record<ProfitAndLossTableFilterView, string> = {
  'non-zero-rows': 'Non-zero Rows',
  'non-zero-columns': 'Non-zero Columns',
};

const View = () => {
  const { filter, setFilter } = useProfitAndLossTableFilter();

  const data = useMemo(() => {
    return Object.entries(VIEW_OPTIONS).map(([value, label]) => ({
      label,
      value: value as ProfitAndLossTableFilterView,
    }));
  }, []);

  const value = data.filter((i) => filter.view?.includes(i.value));

  return (
    <Filter.Select
      label="View"
      type="multiple"
      value={value}
      setValue={(v) =>
        setFilter({ view: v.length ? v.map((i) => i.value) : [] })
      }
      data={{
        options: data,
      }}
    />
  );
};

const Party = () => {
  const { filter, setFilter } = useProfitAndLossTableFilter();

  return (
    <CustomFilter.Party
      party={filter.party}
      setParty={(v) => setFilter({ party: v || null })}
    />
  );
};

const Listings = () => {
  const { filter, setFilter } = useProfitAndLossTableFilter();

  return (
    <CustomFilter.Listings
      listingIds={filter.listingIds ?? []}
      setListingIds={(v) => setFilter({ listingIds: v || null })}
    />
  );
};

export const ProfitAndLossTableFilter = {
  Book,
  Date,
  Listings,
  Party,
  Type,
  View,
};
